import { scrollPaginationTop } from 'utils/url-pagination'
import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Badge, Button, Card, CardBody, Col, Collapse, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { formatPhase3Date, getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'

const truncateText = (value, limit = 96) => {
  if (!value) return '-'

  const text = typeof value === 'string' ? value : JSON.stringify(value)
  return text.length > limit ? `${text.slice(0, limit)}...` : text
}

const displayText = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  return String(value)
}

const getReadableFields = (row, key) => {
  const directFields = row[key]
  const formattedFields = row.formatted?.[key]
  const fields = Array.isArray(directFields) ? directFields : formattedFields

  return Array.isArray(fields) ? fields : []
}

const getChanges = (row) => getReadableFields(row, 'changes')

const getActivityLabel = (row) => row.activity_label || row.formatted?.activity_label || truncateText(row.activity, 72)

const getActivitySentence = (row) => row.activity_sentence || row.formatted?.activity_sentence || getActivityLabel(row)

const getIdentityLabel = (row) => row.identity_label || row.formatted?.identity_label || '-'

const getActorLabel = (row) => row.actor || row.formatted?.actor || row.done_by || '-'

const getSourceLabel = (row) => row.source_label || row.formatted?.source_label || ''

const getSourceColor = (row) => {
  const source = row.source || row.formatted?.source

  if (source === 'stock_opname_new_dashboard') return 'primary'
  if (source === 'stock_opname_old_dashboard') return 'secondary'

  return 'light'
}

const buildInitialFilters = (search = window.location.search) => {
  const params = new URLSearchParams(search)

  return {
    q          : params.get('q') || '',
    scope      : params.get('scope') || 'all',
    activity   : params.get('activity') || '',
    done_by    : params.get('done_by') || '',
    product_id : params.get('product_id') || '',
    from_date  : params.get('from_date') || '',
    to_date    : params.get('to_date') || ''
  }
}

const buildInitialPage = (search = window.location.search) => {
  const page = Number(new URLSearchParams(search).get('page') || 1)

  return Number.isFinite(page) && page > 0 ? Math.floor(page) : 1
}

const buildAuditLogSearch = (filters, page) => {
  const params = new URLSearchParams()

  if (filters.q) params.set('q', filters.q)
  params.set('scope', filters.scope || 'all')
  if (filters.activity) params.set('activity', filters.activity)
  if (filters.done_by) params.set('done_by', filters.done_by)
  if (filters.product_id) params.set('product_id', filters.product_id)
  if (filters.from_date) params.set('from_date', filters.from_date)
  if (filters.to_date) params.set('to_date', filters.to_date)
  params.set('page', String(page))

  return params.toString()
}

const ReadableFieldList = ({ fields, emptyText }) => {
  if (!fields.length) {
    return <div className='text-muted small'>{ emptyText }</div>
  }

  return (
    <div>
      { fields.map((field) => (
        <div key={ field.key || field.label } className='d-flex border-bottom py-2'>
          <div className='text-muted pr-3' style={{ minWidth: 170 }}>{ field.label || field.key }</div>
          <div className='font-weight-bold' style={{ whiteSpace: 'pre-wrap' }}>{ displayText(field.value) }</div>
        </div>
      )) }
    </div>
  )
}

const InlineChangesPreview = ({ changes }) => {
  if (!changes.length) return null

  const previewChanges = changes.slice(0, 3)

  return (
    <div className='mt-2 small'>
      { previewChanges.map((change) => (
        <div key={ change.key || change.label } className='mb-1' style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          <strong>{ change.label || change.key }:</strong>{ ' ' }
          <span className='text-muted'>{ truncateText(displayText(change.before), 64) }</span>{ ' → ' }
          <span>{ truncateText(displayText(change.after), 64) }</span>
        </div>
      )) }
      { changes.length > previewChanges.length && (
        <div className='text-muted'>{ `+${changes.length - previewChanges.length} more change${ changes.length - previewChanges.length === 1 ? '' : 's' }` }</div>
      ) }
    </div>
  )
}

const ReadableChangesTable = ({ changes }) => {
  if (!changes.length) {
    return <div className='text-muted small'>No readable field-level changes detected for this log.</div>
  }

  return (
    <Table responsive size='sm' className='mb-0 bg-white'>
      <thead>
        <tr>
          <th style={{ width: '24%' }}>Field</th>
          <th style={{ width: '38%' }}>Before</th>
          <th style={{ width: '38%' }}>After</th>
        </tr>
      </thead>
      <tbody>
        { changes.map((change) => (
          <tr key={ change.key || change.label }>
            <td className='font-weight-bold'>{ change.label || change.key }</td>
            <td style={{ whiteSpace: 'pre-wrap' }}>{ displayText(change.before) }</td>
            <td style={{ whiteSpace: 'pre-wrap' }}>{ displayText(change.after) }</td>
          </tr>
        )) }
      </tbody>
    </Table>
  )
}

const Phase3AuditLogPage = ({ pageUtils, mainTitle = 'Inbound Journey', subTitle = 'Audit Log' }) => {
  const history = useHistory()
  const location = useLocation()
  const [ filters, setFilters ] = useState(buildInitialFilters)
  const [ rows, setRows ] = useState([])
  const [ meta, setMeta ] = useState({ current_page: 1, total_pages: 1, total_count: 0 })
  const [ isLoading, setIsLoading ] = useState(false)
  const [ openedId, setOpenedId ] = useState(null)
  const requestSequence = useRef(0)

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }))

  const fetchRows = async (page, currentFilters) => {
    const sequence = ++requestSequence.current
    setIsLoading(true)

    try {
      const response = await InboundJourneyApiV2.getAuditLogEntries({
        ...currentFilters,
        page     : page,
        per_page : 20
      })

      if (sequence !== requestSequence.current) return
      setRows(response.data?.data || [])
      setMeta(response.data?.meta || { current_page: page, total_pages: 1, total_count: 0 })
    } catch (error) {
      if (sequence !== requestSequence.current) return
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      if (sequence === requestSequence.current) setIsLoading(false)
    }
  }

  const loadRows = (page = 1, currentFilters = filters) => {
    const search = `?${buildAuditLogSearch(currentFilters, page)}`
    if (search === location.search) fetchRows(page, currentFilters)
    else history.push({ pathname: location.pathname, search })
  }

  useEffect(() => {
    const currentFilters = buildInitialFilters(location.search)
    setFilters(currentFilters)
    fetchRows(buildInitialPage(location.search), currentFilters)
    scrollPaginationTop()
    return () => { requestSequence.current += 1 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ location.search ])

  const handleSubmit = (event) => {
    event.preventDefault()
    loadRows(1)
  }

  const clearFilters = () => {
    const nextFilters = {
      q          : '',
      scope      : 'all',
      activity   : '',
      done_by    : '',
      product_id : '',
      from_date  : '',
      to_date    : ''
    }

    setFilters(nextFilters)
    loadRows(1, nextFilters)
  }

  return (
    <>
       <TitlePage mainTitle={ mainTitle } subTitle={ subTitle } />

      <Card>
        <CardBody>
          <div className='d-flex justify-content-between align-items-start mb-3'>
            <div>
              <h4 className='mb-1'>Audit Log Entries</h4>
              <small className='text-muted'>Track inbound journey actions from product received, studio, appraisal, final check, go-live, Tanda Masuk, and outbound authentication.</small>
            </div>
            <Badge color='info' pill>{ meta.total_count || 0 } logs</Badge>
          </div>

          <Form onSubmit={ handleSubmit }>
            <Row form>
              <Col md='3'>
                <FormGroup>
                  <Label>Search</Label>
                  <Input value={ filters.q } onChange={ (event) => setFilter('q', event.target.value) } placeholder='SKU, product ID, email, activity...' />
                </FormGroup>
              </Col>
              <Col md='2'>
                <FormGroup>
                  <Label>Scope</Label>
                  <Input type='select' value={ filters.scope } onChange={ (event) => setFilter('scope', event.target.value) }>
                    <option value='all'>All Audit Logs</option>
                    <option value='inbound_journey'>Inbound Journey Only</option>
                    <option value='stock_opname_all'>Stock Opname — All Dashboards</option>
                    <option value='stock_opname_new_dashboard'>Stock Opname — New Dashboard</option>
                    <option value='stock_opname_old_dashboard'>Stock Opname — Old Dashboard</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md='2'>
                <FormGroup>
                  <Label>SKU / Product ID</Label>
                  <Input value={ filters.product_id } onChange={ (event) => setFilter('product_id', event.target.value) } placeholder='Example: BKR-HR01...' />
                </FormGroup>
              </Col>
              <Col md='2'>
                <FormGroup>
                  <Label>Actor</Label>
                  <Input value={ filters.done_by } onChange={ (event) => setFilter('done_by', event.target.value) } placeholder='email@...' />
                </FormGroup>
              </Col>
              <Col md='3'>
                <FormGroup>
                  <Label>Activity</Label>
                  <Input value={ filters.activity } onChange={ (event) => setFilter('activity', event.target.value) } placeholder='create_product...' />
                </FormGroup>
              </Col>
            </Row>

            <Row form>
              <Col md='3'>
                <FormGroup>
                  <Label>From Date</Label>
                  <Input type='date' value={ filters.from_date } onChange={ (event) => setFilter('from_date', event.target.value) } />
                </FormGroup>
              </Col>
              <Col md='3'>
                <FormGroup>
                  <Label>To Date</Label>
                  <Input type='date' value={ filters.to_date } onChange={ (event) => setFilter('to_date', event.target.value) } />
                </FormGroup>
              </Col>
              <Col md='6' className='d-flex align-items-end'>
                <FormGroup>
                  <Button color='primary' disabled={ isLoading }>
                    { isLoading ? 'Loading...' : 'Filter Logs' }
                  </Button>
                  <Button type='button' color='secondary' outline className='ml-2' onClick={ clearFilters } disabled={ isLoading }>
                    Clear
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>

      <Card className='mt-4'>
        <CardBody>
          <Table responsive hover size='sm'>
            <thead>
              <tr>
                <th>Identity</th>
                <th>Activity</th>
                <th>What Happened</th>
                <th>Actor</th>
                <th>Created At</th>
                <th />
              </tr>
            </thead>
            <tbody>
              { rows.map((row) => {
                const changes = getChanges(row)
                const beforeFields = getReadableFields(row, 'before_fields')
                const afterFields = getReadableFields(row, 'after_fields')

                return (
                  <React.Fragment key={ row.id }>
                    <tr>
                      <td style={{ minWidth: 190 }}>
                        <div className='font-weight-bold'>{ getIdentityLabel(row) }</div>
                        <small className='text-muted'>SKU / record identity</small>
                      </td>
                      <td style={{ minWidth: 210 }}>
                        <Badge color='secondary' pill>{ getActivityLabel(row) }</Badge>
                        { getSourceLabel(row) && (
                          <div className='mt-1'>
                            <Badge color={ getSourceColor(row) } pill>{ getSourceLabel(row) }</Badge>
                          </div>
                        ) }
                      </td>
                      <td style={{ minWidth: 360 }}>
                        <div>{ getActivitySentence(row) }</div>
                        <small className='text-muted'>{ changes.length } readable change{ changes.length === 1 ? '' : 's' }</small>
                        <InlineChangesPreview changes={ changes } />
                      </td>
                      <td style={{ minWidth: 190 }}>{ getActorLabel(row) }</td>
                      <td style={{ minWidth: 150 }}>{ formatPhase3Date(row.created_at) }</td>
                      <td className='text-right'>
                        <Button color='info' size='sm' outline onClick={ () => setOpenedId(openedId === row.id ? null : row.id) }>
                          { openedId === row.id ? 'Hide' : 'View' }
                        </Button>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan='6' className='p-0 border-0'>
                        <Collapse isOpen={ openedId === row.id }>
                          <div className='p-3 bg-light border-top border-bottom'>
                            <div className='bg-white border rounded p-3 mb-3'>
                              <h6 className='mb-2'>Activity Summary</h6>
                              <div>{ getActivitySentence(row) }</div>
                            </div>

                            <div className='mb-3'>
                              <h6 className='mb-2'>Readable Before / After Diff</h6>
                              <ReadableChangesTable changes={ changes } />
                            </div>

                            <Row>
                              <Col md='6'>
                                <div className='bg-white border rounded p-3 h-100'>
                                  <h6 className='mb-2'>Before</h6>
                                  <ReadableFieldList fields={ beforeFields } emptyText='No readable before data.' />
                                </div>
                              </Col>
                              <Col md='6'>
                                <div className='bg-white border rounded p-3 h-100'>
                                  <h6 className='mb-2'>After</h6>
                                  <ReadableFieldList fields={ afterFields } emptyText='No readable after data.' />
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                )
              }) }
              { rows.length === 0 && (
                <tr>
                  <td colSpan='6' className='text-center text-muted py-4'>{ isLoading ? 'Loading logs...' : 'No audit logs found.' }</td>
                </tr>
              ) }
            </tbody>
          </Table>

          <div className='d-flex justify-content-between align-items-center mt-3'>
            <small className='text-muted'>Page { meta.current_page || 1 } of { meta.total_pages || 1 } · Total { meta.total_count || 0 } logs</small>
            <div>
              <Button color='secondary' size='sm' outline disabled={ isLoading || (meta.current_page || 1) <= 1 } onClick={ () => loadRows((meta.current_page || 1) - 1) }>
                Previous
              </Button>
              <Button color='secondary' size='sm' outline className='ml-2' disabled={ isLoading || (meta.current_page || 1) >= (meta.total_pages || 1) } onClick={ () => loadRows((meta.current_page || 1) + 1) }>
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3AuditLogPage
